import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Settings } from 'lucide-react';
import { getCookiePreferences, setCookiePreferences, type CookiePreferences } from '@/lib/cookie-consent';
import { CookieSettings } from './cookie-settings';

const CookieNotice: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences());

  useEffect(() => {
    // Check if user has already made a choice
    const stored = localStorage.getItem('cookieConsent');
    if (!stored) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(newPreferences);
    setCookiePreferences(newPreferences);
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    setCookiePreferences(preferences);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(newPreferences);
    setCookiePreferences(newPreferences);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div 
        role="dialog"
        aria-labelledby="cookie-notice-title"
        aria-describedby="cookie-notice-description"
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[9999]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-5">
          <div className="flex flex-col lg:grid lg:grid-cols-[1fr,auto,auto] gap-3 lg:gap-6 xl:gap-8 lg:items-center">
            <div className="flex-1 min-w-0 pr-8 lg:pr-12">
              <h2 id="cookie-notice-title" className="sr-only">Cookie Notice</h2>
              <p id="cookie-notice-description" className="text-sm text-gray-700">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking "Accept All," you consent to our use of cookies. 
                <Link 
                  to="/cookies" 
                  className="text-primary hover:text-primary-900 ml-1"
                  aria-label="Learn more about our cookie policy"
                >
                  Learn more
                </Link>
              </p>
            </div>
            <div className="grid grid-cols-2 lg:flex lg:justify-end items-center gap-2 lg:gap-3 xl:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                className="text-primary hover:text-white hover:bg-primary justify-center w-full lg:w-[110px] xl:w-[120px]"
                aria-label="Reject all optional cookies"
              >
                Reject All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="flex items-center gap-2 text-primary hover:text-white hover:bg-primary justify-center w-full lg:w-[110px] xl:w-[120px]"
                aria-label="Open cookie settings"
              >
                <Settings className="h-4 w-4" aria-hidden="true" />
                Settings
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAcceptSelected}
                className="text-primary hover:text-white hover:bg-primary justify-center w-full lg:w-[130px] xl:w-[140px]"
                aria-label="Accept selected cookie preferences"
              >
                Accept Selected
              </Button>
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="text-white hover:text-white justify-center w-full lg:w-[110px] xl:w-[120px]"
                aria-label="Accept all cookies"
              >
                Accept All
              </Button>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="hidden lg:flex items-center justify-center h-9 text-gray-400 hover:text-gray-500 p-1.5"
              aria-label="Close cookie notice"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="lg:hidden absolute top-3 right-3 sm:right-6 text-gray-400 hover:text-gray-500 p-1.5"
            aria-label="Close cookie notice"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      <CookieSettings 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
      />
    </>
  );
};

export default CookieNotice; 